// src/admin/TangoBalancePanel.jsx
import React, { useState, useEffect } from 'react';
import { functions } from '../config/firebase-config';
import { httpsCallable } from 'firebase/functions';
import './TangoBalancePanel.css';

const TangoBalancePanel = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchBalance = async () => {
    try {
      setIsRefreshing(true);
      const getTangoBalance = httpsCallable(functions, 'getTangoBalance');
      const result = await getTangoBalance();
      
      if (result.data.success) {
        setBalance(result.data);
        setError(null);
      } else {
        throw new Error('Failed to fetch balance');
      }
    } catch (err) {
      console.error('Error fetching Tango balance:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    // Refresh balance every 5 minutes
    const interval = setInterval(fetchBalance, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical': return '🚨';
      case 'warning': return '⚠️';
      case 'adequate': return '📊';
      case 'optimal': return '✅';
      case 'excess': return '💰';
      default: return '📈';
    }
  };

  const getProgressPercentage = () => {
    if (!balance) return 0;
    const current = balance.balance.available;
    const comfort = balance.thresholds.COMFORT;
    return Math.min((current / comfort) * 100, 100);
  };

  if (loading && !balance) {
    return (
      <div className="tango-balance-panel loading">
        <h3>Tango Card Balance</h3>
        <div className="spinner">Loading...</div>
      </div>
    );
  }

  if (error && !balance) {
    return (
      <div className="tango-balance-panel error">
        <h3>Tango Card Balance</h3>
        <div className="error-message">
          <p>Failed to load balance</p>
          <button onClick={fetchBalance} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`tango-balance-panel ${balance?.status?.level || ''}`}>
      <div className="panel-header">
        <h3>
          <span className="icon">💳</span>
          Tango Card Balance
        </h3>
        <button 
          onClick={fetchBalance} 
          className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
          disabled={isRefreshing}
          title="Refresh balance"
        >
          🔄
        </button>
      </div>

      {balance && (
        <>
          <div className="balance-display">
            <div className="balance-amount">
              <span className="status-icon">{getStatusIcon(balance.status.level)}</span>
              <span className="amount">{formatCurrency(balance.balance.available)}</span>
            </div>
            <div className={`status-badge ${balance.status.color}`}>
              {balance.status.level.toUpperCase()}
            </div>
          </div>

          <div className="balance-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${getProgressPercentage()}%`,
                  backgroundColor: balance.status.color === 'red' ? '#ff4444' :
                                   balance.status.color === 'orange' ? '#ff9944' :
                                   balance.status.color === 'yellow' ? '#ffcc00' :
                                   balance.status.color === 'green' ? '#44ff44' :
                                   '#4444ff'
                }}
              />
            </div>
            <div className="progress-labels">
              <span>$0</span>
              <span>{formatCurrency(balance.thresholds.COMFORT)}</span>
            </div>
          </div>

          <div className="balance-recommendation">
            <p>{balance.status.recommendation}</p>
          </div>

          <div className="balance-thresholds">
            <h4>Thresholds</h4>
            <div className="threshold-list">
              <div className="threshold-item critical">
                <span>Critical</span>
                <span>{formatCurrency(balance.thresholds.CRITICAL)}</span>
              </div>
              <div className="threshold-item warning">
                <span>Warning</span>
                <span>{formatCurrency(balance.thresholds.WARNING)}</span>
              </div>
              <div className="threshold-item comfort">
                <span>Comfort</span>
                <span>{formatCurrency(balance.thresholds.COMFORT)}</span>
              </div>
              <div className="threshold-item excess">
                <span>Excess</span>
                <span>{formatCurrency(balance.thresholds.EXCESS)}</span>
              </div>
            </div>
          </div>

          {balance.analytics && (
            <div className="balance-analytics">
              <h4>Usage Analytics</h4>
              <div className="analytics-grid">
                <div className="analytics-item">
                  <span className="label">Weekly Spend</span>
                  <span className="value">{formatCurrency(balance.analytics.weeklySpend)}</span>
                </div>
                <div className="analytics-item">
                  <span className="label">Daily Average</span>
                  <span className="value">{formatCurrency(balance.analytics.dailyAverage)}</span>
                </div>
                <div className="analytics-item">
                  <span className="label">Days Remaining</span>
                  <span className="value">
                    {balance.analytics.daysRemaining > 100 ? '100+' : balance.analytics.daysRemaining}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="account-info">
            <h4>Account Info</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Account</span>
                <span className="value">{balance.account.name}</span>
              </div>
              <div className="info-item">
                <span className="label">Status</span>
                <span className="value">{balance.account.status}</span>
              </div>
              <div className="info-item">
                <span className="label">Environment</span>
                <span className="value">PRODUCTION</span>
              </div>
            </div>
          </div>

          <div className="last-checked">
            Last updated: {new Date(balance.lastChecked).toLocaleString()}
          </div>

          <div className="tango-actions">
            <a 
              href="https://www.tangocard.com/customers/sign_in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="tango-link"
            >
              Open Tango Dashboard →
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default TangoBalancePanel;